// Plan 14-01 Wave 0 — Per-mode loadout presets (DEC-024 4-of-8 power-ups).
//
// MUI-03 ships the pre-match Loadout Picker modal. Each mode (casual /
// ranked / friend) has its own last-used 4-power-up preset persisted to
// localStorage so a returning player sees their last choice pre-selected.
//
// Storage layout (one key per mode, JSON-stringified):
//   runegambit.loadout.casual → '["scriveners_quill","tongue_tie","knights_vow","whisperers_promise"]'
//   runegambit.loadout.ranked → '...'
//   runegambit.loadout.friend → '...'
//
// `getLoadoutForMode` defends against three corruption modes (T-14-01-02
// mitigation):
//   1. empty string (never set) → null
//   2. JSON parse error → null
//   3. parsed array of wrong length → null (DEC-024 requires exactly 4)
//
// Server authority is preserved (DEC-021): the LoadoutPicker filters this
// preset against `playerView.available_power_ups[]` at render time, so a
// stale preset (e.g. a power-up that was deprecated) never reaches the
// server.

import { persistentMap } from '@nanostores/persistent'
import type { PowerUpName } from '../game/PlayerViewSchema'

export type LoadoutMode = 'casual' | 'ranked' | 'friend'

/**
 * A 4-of-8 power-up loadout. Length is locked at 4 per DEC-024; the type
 * uses a tuple so misuse is caught at the type layer.
 */
export type Loadout = readonly [
  PowerUpName,
  PowerUpName,
  PowerUpName,
  PowerUpName,
]

/**
 * One subkey per mode. Each subkey holds JSON-stringified Loadout (or '' if
 * never set). `persistentMap` writes each subkey under
 * `runegambit.loadout.<mode>` in localStorage.
 *
 * `string` (not `string | undefined`) keeps the type signature simple; an
 * empty string represents "no preset" (decoder returns null).
 */
export const $loadoutPresets = persistentMap<{
  casual: string
  ranked: string
  friend: string
}>('runegambit.loadout.', {
  casual: '',
  ranked: '',
  friend: '',
})

/**
 * Read the persisted Loadout for a mode. Returns null if unset, corrupted,
 * or of unexpected shape. Plans 04/05 fall back to the DEC-024 casual
 * default when this returns null.
 */
export function getLoadoutForMode(mode: LoadoutMode): Loadout | null {
  const raw = $loadoutPresets.get()[mode]
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    if (parsed.length !== 4) return null
    if (!parsed.every((p) => typeof p === 'string')) return null
    return parsed as unknown as Loadout
  } catch {
    return null
  }
}

/**
 * Persist a Loadout for a mode. Plans 04/05 call this on "Lock in loadout".
 *
 * No validation here — server authority (`available_power_ups[]`) is the
 * gate that prevents an invalid loadout from being submitted with a turn.
 * Storing a malformed loadout would only show up to the user themselves
 * on the next render.
 */
export function setLoadoutForMode(mode: LoadoutMode, loadout: Loadout): void {
  $loadoutPresets.setKey(mode, JSON.stringify(loadout))
}
